import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import { useSelector } from "react-redux";
import Select from "react-select";
import {
  agGridOptionsSingle,
  agGridTheme,
  customStyles,
} from "../../helpers/constants/Constants";
import {
  deleteView,
  getDisciplines,
  getProjectAssetTasks,
  getViews,
  saveView,
} from "../../helpers/axios/projectsAxios";

/* global Autodesk */ // eslint-disable-line

const cloneViewerState = (state) => {
  if (!state) {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(state));
  } catch (err) {
    return null;
  }
};

const normalizeModelReferenceValue = (value) => {
  if (value == null) {
    return "";
  }

  return String(value)
    .trim()
    .replace(/^urn:/i, "")
    .toLowerCase();
};

const getLoadedViewerModels = (viewer) => {
  if (!viewer) {
    return [];
  }

  const visibleModels = viewer.getVisibleModels?.();
  if (Array.isArray(visibleModels) && visibleModels.length) {
    return visibleModels;
  }

  const allModels = viewer.getAllModels?.();
  if (Array.isArray(allModels) && allModels.length) {
    return allModels;
  }

  const queuedModels = viewer.impl?.modelQueue?.getModels?.();
  if (Array.isArray(queuedModels) && queuedModels.length) {
    return queuedModels;
  }

  if (viewer.model) {
    return [viewer.model];
  }

  return [];
};

const getViewerModelReferences = (viewer) => {
  const models = getLoadedViewerModels(viewer);

  return models
    .map((viewerModel) => {
      const documentNode = viewerModel?.getDocumentNode?.();
      const viewerModelData = viewerModel?.getData?.();
      const document = documentNode?.getDocument?.();

      const modelGuidCandidates = [
        document?.myData?.guid,
        documentNode?.getModelKey?.(),
        documentNode?.data?.urn,
        viewerModelData?.urn,
        viewerModelData?.loadOptions?.bubbleNode?.getDocument?.()?.myData?.guid,
        viewerModelData?.loadOptions?.bubbleNode?.data?.urn,
        viewerModel?.myData?.guid,
        viewerModel?.getSeedUrn?.(),
      ];

      const modelNameCandidates = [
        documentNode?.name?.(),
        documentNode?.data?.name,
        viewerModelData?.name,
      ];

      const modelGuid =
        modelGuidCandidates.find((candidate) =>
          Boolean(normalizeModelReferenceValue(candidate)),
        ) || null;
      const modelName =
        modelNameCandidates.find((candidate) => Boolean(String(candidate || "").trim())) ||
        null;

      return {
        modelGuid,
        modelName,
      };
    })
    .filter((modelRef) => modelRef.modelGuid || modelRef.modelName);
};

const getNormalizedModelReferences = (modelReferences) => {
  if (!Array.isArray(modelReferences)) {
    return [];
  }

  return modelReferences
    .map((modelRef) => {
      if (typeof modelRef === "string") {
        return {
          modelGuid: modelRef,
          modelName: null,
        };
      }

      return {
        modelGuid: modelRef?.modelGuid || null,
        modelName: modelRef?.modelName || null,
      };
    })
    .filter(
      (modelRef) =>
        Boolean(normalizeModelReferenceValue(modelRef?.modelGuid)) ||
        Boolean(String(modelRef?.modelName || "").trim()),
    );
};

const getProjectAssetTaskModelIds = (projectAssetTask) => {
  if (!projectAssetTask || typeof projectAssetTask !== "object") {
    return [];
  }

  const ids = [projectAssetTask?.modelId, projectAssetTask?.urn];

  if (Array.isArray(projectAssetTask?.versions)) {
    projectAssetTask.versions.forEach((version) => {
      ids.push(version?.modelId, version?.urn);
    });
  }

  return [...new Set(ids.filter((id) => Boolean(normalizeModelReferenceValue(id))))];
};

const getProjectAssetTaskLookupByModelId = (projectAssetTasks) => {
  const lookup = new Map();

  (Array.isArray(projectAssetTasks) ? projectAssetTasks : []).forEach((projectAssetTask) => {
    getProjectAssetTaskModelIds(projectAssetTask).forEach((modelId) => {
      const modelKey = normalizeModelReferenceValue(modelId);
      if (!modelKey || lookup.has(modelKey)) {
        return;
      }

      lookup.set(modelKey, projectAssetTask);
    });
  });

  return lookup;
};

const getProjectAssetTaskKey = (projectAssetTask) => {
  if (!projectAssetTask || typeof projectAssetTask !== "object") {
    return "";
  }

  const idKey = projectAssetTask?.id != null ? String(projectAssetTask.id).trim() : "";
  if (idKey) {
    return `task-id:${idKey}`;
  }

  const docNumberKey = String(projectAssetTask?.docNumber || "").trim().toLowerCase();
  const taskNameKey = String(projectAssetTask?.task || "").trim().toLowerCase();
  const disciplineKey = String(projectAssetTask?.discipline || "")
    .trim()
    .toLowerCase();

  const composedKey = [docNumberKey, taskNameKey, disciplineKey]
    .filter(Boolean)
    .join("|");

  return composedKey ? `task-meta:${composedKey}` : "";
};

const getProjectAssetTaskDisplayName = (projectAssetTask) => {
  if (!projectAssetTask) {
    return "Unmapped model";
  }

  return (
    projectAssetTask?.task ||
    projectAssetTask?.docNumber ||
    projectAssetTask?.documentName ||
    projectAssetTask?.name ||
    "Unnamed task"
  );
};

const normalizeSnapshotValue = (value) => {
  return String(value ?? "").trim().toLowerCase();
};

const buildTaskStatusRevisionSnapshot = ({ modelReferences, projectAssetTasks }) => {
  const taskLookupByModelId = getProjectAssetTaskLookupByModelId(projectAssetTasks);
  const snapshotByTaskKey = new Map();

  (Array.isArray(modelReferences) ? modelReferences : []).forEach((modelRef) => {
    const normalizedModelGuid = normalizeModelReferenceValue(modelRef?.modelGuid);
    if (!normalizedModelGuid) {
      return;
    }

    const projectAssetTask = taskLookupByModelId.get(normalizedModelGuid);
    if (!projectAssetTask) {
      return;
    }

    const taskKey = getProjectAssetTaskKey(projectAssetTask);
    if (!taskKey || snapshotByTaskKey.has(taskKey)) {
      return;
    }

    snapshotByTaskKey.set(taskKey, {
      taskKey,
      item: getProjectAssetTaskDisplayName(projectAssetTask),
      docNumber: String(projectAssetTask?.docNumber || ""),
      discipline: String(projectAssetTask?.discipline || ""),
      status: String(projectAssetTask?.status || ""),
      publishedRevision: String(projectAssetTask?.publishedRevision || ""),
      sharedRevision: String(projectAssetTask?.sharedRevision || ""),
    });
  });

  return [...snapshotByTaskKey.values()];
};

const normalizeStoredTaskStatusRevisionSnapshot = (snapshot) => {
  if (!Array.isArray(snapshot)) {
    return [];
  }

  return snapshot
    .map((entry) => {
      const taskKey = String(entry?.taskKey || "").trim();
      if (!taskKey) {
        return null;
      }

      return {
        taskKey,
        item: String(entry?.item || "").trim() || "Unnamed task",
        docNumber: String(entry?.docNumber || ""),
        discipline: String(entry?.discipline || ""),
        status: String(entry?.status || ""),
        publishedRevision: String(entry?.publishedRevision || ""),
        sharedRevision: String(entry?.sharedRevision || ""),
      };
    })
    .filter(Boolean);
};

const formatTaskStatusRevisionSummary = (entry) => {
  const details = [
    entry?.status ? `Status ${entry.status}` : "Status -",
    entry?.publishedRevision ? `Pub Rev ${entry.publishedRevision}` : "Pub Rev -",
    entry?.sharedRevision ? `Shared Rev ${entry.sharedRevision}` : "Shared Rev -",
  ];

  return details.join(" | ");
};

const buildTaskStatusRevisionChangeRows = ({ savedSnapshot, currentSnapshot }) => {
  const savedByTaskKey = new Map();
  (Array.isArray(savedSnapshot) ? savedSnapshot : []).forEach((entry) => {
    if (entry?.taskKey && !savedByTaskKey.has(entry.taskKey)) {
      savedByTaskKey.set(entry.taskKey, entry);
    }
  });

  const currentByTaskKey = new Map();
  (Array.isArray(currentSnapshot) ? currentSnapshot : []).forEach((entry) => {
    if (entry?.taskKey && !currentByTaskKey.has(entry.taskKey)) {
      currentByTaskKey.set(entry.taskKey, entry);
    }
  });

  const rows = [];
  savedByTaskKey.forEach((savedEntry, taskKey) => {
    const currentEntry = currentByTaskKey.get(taskKey);
    if (!currentEntry) {
      return;
    }

    const publishedRevisionChanged =
      normalizeSnapshotValue(savedEntry.publishedRevision) !==
      normalizeSnapshotValue(currentEntry.publishedRevision);
    const sharedRevisionChanged =
      normalizeSnapshotValue(savedEntry.sharedRevision) !==
      normalizeSnapshotValue(currentEntry.sharedRevision);

    if (!publishedRevisionChanged && !sharedRevisionChanged) {
      return;
    }

    rows.push({
      id: `changed-${taskKey}`,
      changeType: "Revision",
      docNumber:
        savedEntry.docNumber || currentEntry.docNumber || "No document number",
      before: formatTaskStatusRevisionSummary(savedEntry),
      after: formatTaskStatusRevisionSummary(currentEntry),
    });
  });

  return rows;
};

const getModelReferenceKeySet = (modelReferences) => {
  return new Set(
    (Array.isArray(modelReferences) ? modelReferences : [])
      .map((modelRef) => {
        const guidKey =
          typeof modelRef === "string"
            ? normalizeModelReferenceValue(modelRef)
            : normalizeModelReferenceValue(modelRef?.modelGuid);
        if (guidKey) {
          return `guid:${guidKey}`;
        }

        return "";
      })
      .filter(Boolean),
  );
};

const getModelReferencesFromViewerState = (savedViewerState) => {
  if (!savedViewerState || typeof savedViewerState !== "object") {
    return [];
  }

  const modelGuidCandidates = [
    savedViewerState?.seedURN,
    savedViewerState?.seedUrn,
    ...(Array.isArray(savedViewerState?.objectSet)
      ? savedViewerState.objectSet.flatMap((entry) => [entry?.seedURN, entry?.seedUrn])
      : []),
  ].filter((candidate) => Boolean(normalizeModelReferenceValue(candidate)));

  const uniqueGuidMap = new Map();
  modelGuidCandidates.forEach((candidate) => {
    const key = normalizeModelReferenceValue(candidate);
    if (key && !uniqueGuidMap.has(key)) {
      uniqueGuidMap.set(key, String(candidate));
    }
  });

  return [...uniqueGuidMap.values()];
};

const getPrimaryModelGuid = (savedView) => {
  const explicitPrimary = savedView?.primaryModelGuid;
  if (normalizeModelReferenceValue(explicitPrimary)) {
    return explicitPrimary;
  }

  const fromState = savedView?.viewerState?.seedURN || savedView?.viewerState?.seedUrn;
  if (normalizeModelReferenceValue(fromState)) {
    return fromState;
  }

  const fromModelReferences = savedView?.modelReferences?.find((modelRef) =>
    Boolean(
      normalizeModelReferenceValue(
        typeof modelRef === "string" ? modelRef : modelRef?.modelGuid,
      ),
    ),
  );

  const normalizedFromModelReferences =
    typeof fromModelReferences === "string"
      ? fromModelReferences
      : fromModelReferences?.modelGuid;

  if (normalizeModelReferenceValue(normalizedFromModelReferences)) {
    return normalizedFromModelReferences;
  }

  return null;
};

const bindViewerStateToPrimaryModel = (savedViewerState, primaryModelGuid) => {
  const viewerClone = cloneViewerState(savedViewerState);
  if (!viewerClone || !normalizeModelReferenceValue(primaryModelGuid)) {
    return viewerClone;
  }

  viewerClone.seedURN = primaryModelGuid;
  viewerClone.seedUrn = primaryModelGuid;

  if (Array.isArray(viewerClone.objectSet)) {
    viewerClone.objectSet = viewerClone.objectSet.map((entry) => {
      if (!entry || typeof entry !== "object") {
        return entry;
      }

      const entrySeed = entry.seedURN || entry.seedUrn;
      if (normalizeModelReferenceValue(entrySeed)) {
        return entry;
      }

      return {
        ...entry,
        seedURN: primaryModelGuid,
      };
    });
  }

  return viewerClone;
};

// Drops objectSet entries for models not currently loaded (e.g. a model updated to a new version since the view was saved).
const bindViewerStateToLoadedModels = (savedViewerState, primaryModelGuid, loadedModelKeys) => {
  const viewerClone = bindViewerStateToPrimaryModel(savedViewerState, primaryModelGuid);
  if (!viewerClone || typeof viewerClone !== "object") {
    return viewerClone;
  }

  const isLoaded = (urn) =>
    Boolean(normalizeModelReferenceValue(urn)) &&
    loadedModelKeys.has(`guid:${normalizeModelReferenceValue(urn)}`);

  if (Array.isArray(viewerClone.objectSet)) {
    viewerClone.objectSet = viewerClone.objectSet.filter(
      (entry) =>
        !entry || typeof entry !== "object" || isLoaded(entry.seedURN || entry.seedUrn),
    );
  }

  if (!isLoaded(viewerClone.seedURN || viewerClone.seedUrn)) {
    const fallbackSeed = (viewerClone.objectSet || [])
      .map((entry) => entry?.seedURN || entry?.seedUrn)
      .find(isLoaded);

    if (fallbackSeed) {
      viewerClone.seedURN = fallbackSeed;
      viewerClone.seedUrn = fallbackSeed;
    } else {
      delete viewerClone.seedURN;
      delete viewerClone.seedUrn;
    }
  }

  return viewerClone;
};

const getCameraOnlyViewerState = (savedViewerState) => {
  const viewerClone = cloneViewerState(savedViewerState);
  if (!viewerClone || typeof viewerClone !== "object") {
    return viewerClone;
  }

  delete viewerClone.seedURN;
  delete viewerClone.seedUrn;

  if (Array.isArray(viewerClone.objectSet)) {
    viewerClone.objectSet = [];
  }

  delete viewerClone.cutplanes;

  return viewerClone;
};

const getViewerStateWithoutViewport = (savedViewerState) => {
  const viewerClone = cloneViewerState(savedViewerState);
  if (!viewerClone || typeof viewerClone !== "object") {
    return viewerClone;
  }

  delete viewerClone.viewport;
  return viewerClone;
};

const captureViewerPreferences = (viewer) => {
  const prefs = viewer?.prefs;
  if (!prefs) {
    return null;
  }

  const rawPreferenceBagCandidates = [
    prefs._values,
    prefs.preferences,
    prefs._preferences,
    prefs._prefMap,
    prefs._prefs,
    prefs._store,
  ];

  const rawPreferenceBag = rawPreferenceBagCandidates.find(
    (candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate),
  );

  if (!rawPreferenceBag) {
    return null;
  }

  const snapshot = {};
  Object.keys(rawPreferenceBag).forEach((key) => {
    if (!key) {
      return;
    }

    try {
      const value = typeof prefs.get === "function" ? prefs.get(key) : rawPreferenceBag[key];
      if (typeof value !== "function") {
        snapshot[key] = value;
      }
    } catch (err) {
      const fallbackValue = rawPreferenceBag[key];
      if (typeof fallbackValue !== "function") {
        snapshot[key] = fallbackValue;
      }
    }
  });

  return Object.keys(snapshot).length ? snapshot : null;
};

const restoreViewerPreferences = (viewer, preferenceSnapshot) => {
  const prefs = viewer?.prefs;
  if (!prefs || !preferenceSnapshot || typeof preferenceSnapshot !== "object") {
    return;
  }

  Object.entries(preferenceSnapshot).forEach(([key, value]) => {
    if (!key || typeof prefs.set !== "function") {
      return;
    }

    try {
      prefs.set(key, value);
    } catch (err) {
      try {
        prefs.set(key, value, true);
      } catch (setErr) {
        // Ignore unsupported preference keys for this viewer build.
      }
    }
  });
};

const clearViewerSectionArtifacts = async (viewer) => {
  if (!viewer) {
    return;
  }

  const runSectionToolExitCycle = async () => {
    let sectionExtension = viewer.getExtension?.("Autodesk.Section");

    if (!sectionExtension && typeof viewer.loadExtension === "function") {
      try {
        sectionExtension = await viewer.loadExtension("Autodesk.Section");
      } catch (err) {
        sectionExtension = null;
      }
    }

    // Mimic manual section-tool teardown path as closely as possible.
    try {
      sectionExtension?.activate?.("X");
    } catch (err) {
      // Ignore if mode is unsupported in this viewer build.
    }

    sectionExtension?.deactivate?.();
    sectionExtension?.clearSection?.();

    viewer.toolController?.deactivateTool?.("section");
    viewer.toolController?.deactivateTool?.("sectionBox");
    viewer.toolController?.deactivateTool?.("sectionbox");
  };

  const clearOnce = async () => {
    await runSectionToolExitCycle();

    viewer.setCutPlanes?.([]);
    viewer.setCutPlanes?.(null);
    viewer.impl?.setCutPlanes?.([]);
    viewer.impl?.setCutPlanes?.(null);

    await runSectionToolExitCycle();

    viewer.impl?.sceneUpdated?.(true);
    viewer.impl?.invalidate?.(true, true, true);
  };

  await clearOnce();

  await new Promise((resolve) => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => resolve());
      return;
    }

    setTimeout(resolve, 0);
  });

  await clearOnce();
};

const formatViewDate = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatViewDateLabel = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString("en-GB");
};

const Views = ({ viewerState, isPanelCollapsed = false }) => {
  const { selectedProject, userDetails } = useSelector((state) => state.globalReducer);
  const userData = useSelector((state) => state.userData.data);
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
  const [saveViewTitle, setSaveViewTitle] = useState("");
  const [saveViewDiscipline, setSaveViewDiscipline] = useState(null);
  const [disciplineOptions, setDisciplineOptions] = useState([]);
  const [saveViewBusy, setSaveViewBusy] = useState(false);
  const [applyViewBusyId, setApplyViewBusyId] = useState(null);
  const [capturedViewState, setCapturedViewState] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [viewFilterCat, setViewFilterCat] = useState(null);
  const [viewFilterValue, setViewFilterValue] = useState("");
  const [savedViewsBusy, setSavedViewsBusy] = useState(false);
  const [projectAssetTasks, setProjectAssetTasks] = useState([]);
  const [viewerModelStateTick, setViewerModelStateTick] = useState(0);
  const [deleteViewModalOpen, setDeleteViewModalOpen] = useState(false);
  const [pendingDeleteView, setPendingDeleteView] = useState(null);
  const [deleteViewBusy, setDeleteViewBusy] = useState(false);
  const [exitViewBusy, setExitViewBusy] = useState(false);
  const [modelChangeInfoModalOpen, setModelChangeInfoModalOpen] = useState(false);
  const [selectedViewForModelChangeInfo, setSelectedViewForModelChangeInfo] = useState(null);
  const preAppliedViewContextRef = useRef(null);

  const projectRegionCode = selectedProject?.[0]?.mviewProjectInfo?.regionCode;
  const creatorName =
    `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim() ||
    userData?.emailId ||
    "Unknown user";
  const creatorUserId = userDetails?.id || null;

  const currentViewerModelKeys = useMemo(() => {
    // Depend on viewerModelStateTick so this recomputes when viewer models change.
    void viewerModelStateTick;
    return getModelReferenceKeySet(getViewerModelReferences(viewerState?.viewer));
  }, [viewerState, viewerModelStateTick]);

  const currentViewerTaskStatusRevisionSnapshot = useMemo(() => {
    // Depend on viewerModelStateTick so this recomputes when viewer models change.
    void viewerModelStateTick;

    const currentReferences = getNormalizedModelReferences(
      getViewerModelReferences(viewerState?.viewer),
    );

    return buildTaskStatusRevisionSnapshot({
      modelReferences: currentReferences,
      projectAssetTasks,
    });
  }, [viewerState, viewerModelStateTick, projectAssetTasks]);

  const taskStatusRevisionChangesByViewId = useMemo(() => {
    const changesById = new Map();

    savedViews.forEach((savedView) => {
      const savedSnapshot = Array.isArray(savedView?.taskStatusRevisionSnapshot) &&
        savedView.taskStatusRevisionSnapshot.length
        ? normalizeStoredTaskStatusRevisionSnapshot(savedView.taskStatusRevisionSnapshot)
        : [];

      const changeRows = buildTaskStatusRevisionChangeRows({
        savedSnapshot,
        currentSnapshot: currentViewerTaskStatusRevisionSnapshot,
      });

      changesById.set(savedView.id, changeRows);
    });

    return changesById;
  }, [savedViews, currentViewerTaskStatusRevisionSnapshot]);

  const creatorOptions = useMemo(() => {
    return [...new Set(savedViews.map((savedView) => savedView?.createdBy).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map((creator) => ({ value: creator, label: creator }));
  }, [savedViews]);

  const createdDateOptions = useMemo(() => {
    return [...new Set(savedViews.map((savedView) => formatViewDate(savedView?.createdAt)).filter(Boolean))]
      .sort((a, b) => b.localeCompare(a))
      .map((createdDate) => ({
        value: createdDate,
        label: new Date(createdDate).toLocaleDateString("en-GB"),
      }));
  }, [savedViews]);

  const viewFilterOptions = useMemo(() => {
    return [
      { value: "", label: "No Filter" },
      { value: "title", label: "Title" },
      { value: "discipline", label: "Discipline" },
      { value: "creator", label: "Created by" },
      { value: "createdDate", label: "Created date" },
    ];
  }, []);

  const availableDisciplineOptions = useMemo(() => {
    const savedDisciplineCodes = new Set(
      savedViews
        .map((savedView) => savedView?.disciplineCode)
        .filter(Boolean),
    );

    return disciplineOptions.filter((option) => savedDisciplineCodes.has(option.value));
  }, [disciplineOptions, savedViews]);

  const filteredViews = useMemo(() => {
    const normalizedFilterValue = String(viewFilterValue || "").trim().toLowerCase();

    if (!viewFilterCat?.value || !normalizedFilterValue) {
      return savedViews;
    }

    return savedViews.filter((savedView) => {
      switch (viewFilterCat.value) {
        case "title":
          return savedView?.title?.toLowerCase().includes(normalizedFilterValue);
        case "discipline":
          return (
            String(savedView?.disciplineCode || "").toLowerCase() ===
            normalizedFilterValue
          );
        case "creator":
          return String(savedView?.createdBy || "").toLowerCase() === normalizedFilterValue;
        case "createdDate":
          return formatViewDate(savedView?.createdAt) === viewFilterValue;
        default:
          return true;
      }
    });
  }, [savedViews, viewFilterCat, viewFilterValue]);

  const hasActiveFilters = !!viewFilterCat?.value && !!String(viewFilterValue || "").trim();

  const modelChangeGridColumnDefs = useMemo(
    () => [
      {
        headerName: "Change",
        field: "changeType",
        minWidth: 120,
        flex: 0.8,
      },
      {
        headerName: "Document Number",
        field: "docNumber",
        minWidth: 210,
        flex: 1.2,
      },
      {
        headerName: "Previous Data",
        field: "before",
        minWidth: 220,
        flex: 1.2,
      },
      {
        headerName: "Current Data",
        field: "after",
        minWidth: 220,
        flex: 1.2,
      },
    ],
    [],
  );

  const modelChangeRows = useMemo(() => {
    const savedView = selectedViewForModelChangeInfo;
    if (!savedView) {
      return [];
    }

    return taskStatusRevisionChangesByViewId.get(savedView.id) || [];
  }, [selectedViewForModelChangeInfo, taskStatusRevisionChangesByViewId]);

  useEffect(() => {
    let isActive = true;

    const loadDisciplines = async () => {
      if (!projectRegionCode) {
        setDisciplineOptions([]);
        return;
      }

      try {
        const disciplines = await getDisciplines(projectRegionCode);
        const sortedDisciplines = [...disciplines].sort((a, b) =>
          (a?.description || "").localeCompare(b?.description || ""),
        );

        if (!isActive) {
          return;
        }

        setDisciplineOptions(
          sortedDisciplines.map((discipline) => ({
            value: discipline?.code || discipline?.description,
            label: discipline?.description || discipline?.code || "Unknown",
            data: discipline,
          })),
        );
      } catch (err) {
        if (!isActive) {
          return;
        }

        setDisciplineOptions([]);
      }
    };

    loadDisciplines();

    return () => {
      isActive = false;
    };
  }, [projectRegionCode]);

  useEffect(() => {
    let isActive = true;

    const loadViews = async () => {
      const project = selectedProject?.[0];
      const projectId = project?.id;

      if (!projectId) {
        if (isActive) {
          setSavedViews([]);
          setActiveViewId(null);
        }
        return;
      }

      if (isActive) {
        setSavedViewsBusy(true);
      }

      try {
        const response = await getViews(projectId);
        if (!isActive) {
          return;
        }

        const rawViews = Array.isArray(response?.data) ? response.data : [];
        const nextViews = rawViews.map((view) => {
          const hasModelReferences = Array.isArray(view?.modelReferences) &&
            view.modelReferences.length > 0;
          const primaryModelGuid = getPrimaryModelGuid(view);

          if (hasModelReferences) {
            return {
              ...view,
              taskStatusRevisionSnapshot: normalizeStoredTaskStatusRevisionSnapshot(
                view?.taskStatusRevisionSnapshot,
              ),
              primaryModelGuid,
            };
          }

          return {
            ...view,
            modelReferences: getModelReferencesFromViewerState(view?.viewerState),
            taskStatusRevisionSnapshot: normalizeStoredTaskStatusRevisionSnapshot(
              view?.taskStatusRevisionSnapshot,
            ),
            primaryModelGuid,
          };
        });
        setSavedViews(nextViews);
      } catch (err) {
        if (!isActive) {
          return;
        }

        setSavedViews([]);
      } finally {
        if (isActive) {
          setSavedViewsBusy(false);
        }
      }
    };

    loadViews();

    return () => {
      isActive = false;
    };
  }, [selectedProject]);

  useEffect(() => {
    let isActive = true;

    const loadProjectAssetTasks = async () => {
      const projectId = selectedProject?.[0]?.id;
      if (!projectId) {
        if (isActive) {
          setProjectAssetTasks([]);
        }
        return;
      }

      try {
        const fetchedTasks = await getProjectAssetTasks(projectId);
        if (!isActive) {
          return;
        }

        setProjectAssetTasks(Array.isArray(fetchedTasks) ? fetchedTasks : []);
      } catch (err) {
        if (!isActive) {
          return;
        }

        setProjectAssetTasks([]);
      }
    };

    loadProjectAssetTasks();

    return () => {
      isActive = false;
    };
  }, [selectedProject]);

  useEffect(() => {
    const viewer = viewerState?.viewer;
    if (!viewer || typeof viewer.addEventListener !== "function") {
      return undefined;
    }

    const updateModelStateTick = () => {
      setViewerModelStateTick((prev) => prev + 1);
    };

    const eventIds = [
      Autodesk?.Viewing?.MODEL_ADDED_EVENT,
      Autodesk?.Viewing?.MODEL_REMOVED_EVENT,
      Autodesk?.Viewing?.GEOMETRY_LOADED_EVENT,
    ].filter((eventId) => Number.isFinite(eventId));

    eventIds.forEach((eventId) => {
      viewer.addEventListener(eventId, updateModelStateTick);
    });

    return () => {
      eventIds.forEach((eventId) => {
        viewer.removeEventListener(eventId, updateModelStateTick);
      });
    };
  }, [viewerState]);

  const resetSaveViewForm = () => {
    setSaveViewTitle("");
    setSaveViewDiscipline(null);
    setCapturedViewState(null);
  };

  const openSaveViewModal = async () => {
    const viewer = viewerState?.viewer;
    if (!viewer) {
      return;
    }

    let currentState = null;
    try {
      currentState = await viewer.getState({
        viewport: true,
        objectSet: true,
        renderOptions: true,
        cutplanes: true,
        seedURN: true,
        explodeScale: true,
      });
    } catch (err) {
      currentState = null;
    }

    setCapturedViewState(cloneViewerState(currentState));
    setSaveViewTitle("");
    setSaveViewDiscipline(null);
    setSaveViewModalOpen(true);
  };

  const closeSaveViewModal = () => {
    if (saveViewBusy) {
      return;
    }

    setSaveViewModalOpen(false);
    resetSaveViewForm();
  };

  const submitSaveViewModal = async () => {
    const trimmedTitle = saveViewTitle.trim();
    const normalizedState = cloneViewerState(capturedViewState);
    const project = selectedProject?.[0];

    if (!trimmedTitle || !normalizedState || !saveViewDiscipline) {
      return;
    }

    setSaveViewBusy(true);
    try {
      const createdAt = new Date().toISOString();
      const selectedDiscipline = saveViewDiscipline?.data || null;
      const modelReferences = getModelReferencesFromViewerState(normalizedState);
      const normalizedModelReferences = getNormalizedModelReferences(modelReferences);
      const taskStatusRevisionSnapshot = buildTaskStatusRevisionSnapshot({
        modelReferences: normalizedModelReferences,
        projectAssetTasks,
      });
      const primaryModelGuid =
        normalizedState?.seedURN || normalizedState?.seedUrn || modelReferences?.[0] || null;
      const viewPayload = {
        projectId: project?.id || null,
        title: trimmedTitle,
        disciplineId: selectedDiscipline?.id || null,
        viewerState: normalizedState,
        modelReferences,
        taskStatusRevisionSnapshot,
        primaryModelGuid,
        createdAt,
        createdByUserId: creatorUserId,
      };

      const saveResponse = await saveView(viewPayload);
      const savedView = saveResponse?.data;

      if (savedView) {
        setSavedViews((prevSavedViews) => [
          {
            ...viewPayload,
            ...savedView,
            createdBy: savedView?.createdBy || creatorName,
          },
          ...prevSavedViews,
        ]);
      }

      setSaveViewModalOpen(false);
      resetSaveViewForm();
    } finally {
      setSaveViewBusy(false);
    }
  };

  const applySavedView = async (savedView) => {
    if (!savedView?.viewerState || !viewerState?.viewer) {
      return;
    }

    const restoreViewerStateSnapshot = async (stateSnapshot) => {
      if (!stateSnapshot) {
        return false;
      }

      try {
        const didRestoreViaViewer = Boolean(
          viewerState.viewer.restoreState?.(cloneViewerState(stateSnapshot)),
        );

        if (didRestoreViaViewer) {
          return true;
        }
      } catch (err) {
        // Fallback below handles viewer builds where restore signatures differ.
      }

      const markupExtension = await viewerState.viewer.loadExtension?.(
        "Autodesk.Viewing.MarkupsCore",
      );

      return Boolean(
        markupExtension?.viewer?.restoreState?.(cloneViewerState(stateSnapshot)),
      );
    };

    const resetViewerToPreAppliedBaseline = async ({ preserveViewport = false } = {}) => {
      const preAppliedContext = preAppliedViewContextRef.current;
      const preAppliedState = preAppliedContext?.viewerState;

      if (preAppliedState) {
        const baselineState = preserveViewport
          ? getViewerStateWithoutViewport(preAppliedState)
          : preAppliedState;
        await restoreViewerStateSnapshot(baselineState);
      }

      await clearViewerSectionArtifacts(viewerState.viewer);
      viewerState.viewer.showAll?.();
      viewerState.viewer.isolate?.([]);
      restoreViewerPreferences(viewerState.viewer, preAppliedContext?.preferences);
      viewerState.viewer.impl?.invalidate?.(true, true, true);
    };

    if (!preAppliedViewContextRef.current) {
      let preApplyState = null;
      try {
        preApplyState = await viewerState.viewer.getState({
          viewport: true,
          objectSet: true,
          renderOptions: true,
          cutplanes: true,
          seedURN: true,
          explodeScale: true,
        });
      } catch (err) {
        preApplyState = null;
      }

      preAppliedViewContextRef.current = {
        viewerState: cloneViewerState(preApplyState),
        preferences: captureViewerPreferences(viewerState.viewer),
      };
    }

    const primaryModelGuid = getPrimaryModelGuid(savedView);

    const savedViewModelKeys = getModelReferenceKeySet(
      Array.isArray(savedView?.modelReferences) && savedView.modelReferences.length
        ? savedView.modelReferences
        : getModelReferencesFromViewerState(savedView?.viewerState),
    );
    const hasModelContext = savedViewModelKeys.size > 0;
    const hasPrimaryModel = Boolean(normalizeModelReferenceValue(primaryModelGuid));
    const isModelCompatible =
      hasModelContext &&
      hasPrimaryModel &&
      [...savedViewModelKeys].some((modelKey) => currentViewerModelKeys.has(modelKey));

    setApplyViewBusyId(savedView.id);
    try {
      if (activeViewId && activeViewId !== savedView.id) {
        await resetViewerToPreAppliedBaseline({ preserveViewport: true });
      }

      const viewerClone = isModelCompatible
        ? bindViewerStateToLoadedModels(
            savedView.viewerState,
            primaryModelGuid,
            currentViewerModelKeys,
          )
        : getCameraOnlyViewerState(savedView.viewerState);
      let didRestoreViaViewer = await restoreViewerStateSnapshot(viewerClone);

      if (!didRestoreViaViewer && isModelCompatible) {
        didRestoreViaViewer = await restoreViewerStateSnapshot(
          getCameraOnlyViewerState(savedView.viewerState),
        );
      }

      const savedCutplanes = savedView?.viewerState?.cutplanes;
      if (!Array.isArray(savedCutplanes) || savedCutplanes.length === 0) {
        await clearViewerSectionArtifacts(viewerState.viewer);
      }

      viewerState.viewer.impl?.invalidate?.(true, true, true);
      setActiveViewId(savedView.id);
    } finally {
      setApplyViewBusyId(null);
    }
  };

  const exitAppliedView = async () => {
    if (!viewerState?.viewer || exitViewBusy) {
      return;
    }

    const preAppliedContext = preAppliedViewContextRef.current;
    setExitViewBusy(true);

    try {
      if (preAppliedContext?.viewerState) {
        try {
          const didRestoreViaViewer = Boolean(
            viewerState.viewer.restoreState?.(
              cloneViewerState(preAppliedContext.viewerState),
            ),
          );

          if (!didRestoreViaViewer) {
            const markupExtension = await viewerState.viewer.loadExtension?.(
              "Autodesk.Viewing.MarkupsCore",
            );
            markupExtension?.viewer?.restoreState?.(
              cloneViewerState(preAppliedContext.viewerState),
            );
          }
        } catch (err) {
          const markupExtension = await viewerState.viewer.loadExtension?.(
            "Autodesk.Viewing.MarkupsCore",
          );
          markupExtension?.viewer?.restoreState?.(
            cloneViewerState(preAppliedContext.viewerState),
          );
        }
      }

      await clearViewerSectionArtifacts(viewerState.viewer);
      viewerState.viewer.showAll?.();
      viewerState.viewer.isolate?.([]);
      restoreViewerPreferences(viewerState.viewer, preAppliedContext?.preferences);
      viewerState.viewer.impl?.invalidate?.(true, true, true);
      setActiveViewId(null);
      preAppliedViewContextRef.current = null;
    } finally {
      setExitViewBusy(false);
    }
  };

  const deleteSavedView = async (savedViewId) => {
    const project = selectedProject?.[0];
    const projectId = project?.id;
    if (!projectId) {
      return;
    }

    setDeleteViewBusy(true);
    try {
      await deleteView(savedViewId, projectId);

      setSavedViews((prevSavedViews) =>
        prevSavedViews.filter((savedView) => savedView.id !== savedViewId),
      );

      if (activeViewId === savedViewId) {
        setActiveViewId(null);
      }
    } catch (err) {
      // Keep existing local state if backend deletion fails.
    } finally {
      setDeleteViewBusy(false);
    }
  };

  const openDeleteViewModal = (savedView) => {
    if (!savedView || deleteViewBusy) {
      return;
    }

    setPendingDeleteView(savedView);
    setDeleteViewModalOpen(true);
  };

  const openModelChangeInfoModal = (savedView) => {
    if (!savedView) {
      return;
    }

    setSelectedViewForModelChangeInfo(savedView);
    setModelChangeInfoModalOpen(true);
  };

  const closeModelChangeInfoModal = () => {
    setModelChangeInfoModalOpen(false);
    setSelectedViewForModelChangeInfo(null);
  };

  const closeDeleteViewModal = () => {
    if (deleteViewBusy) {
      return;
    }

    setDeleteViewModalOpen(false);
    setPendingDeleteView(null);
  };

  const confirmDeleteSavedView = async () => {
    if (!pendingDeleteView?.id || deleteViewBusy) {
      return;
    }

    await deleteSavedView(pendingDeleteView.id);
    setDeleteViewModalOpen(false);
    setPendingDeleteView(null);
  };

  const resetFilters = () => {
    setViewFilterCat(null);
    setViewFilterValue("");
  };

  const viewFilterHandler = (option) => {
    setViewFilterCat(option && option.value ? option : null);
    setViewFilterValue("");
  };

  const viewerOverlayRoot = useMemo(() => {
    if (typeof document === "undefined") {
      return null;
    }

    return viewerState?.viewer?.container || null;
  }, [viewerState]);

  return (
    <>
      <div className="screenshot-markup" style={{ padding: "10px" }}>
        <style>
          {`@keyframes mwhMarkupPing {
            0% {
              box-shadow: 0 0 0 0 rgba(147, 213, 0, 0.65), 0 0 0 0 rgba(147, 213, 0, 0.35);
              transform: scale(1);
            }
            60% {
              box-shadow: 0 0 0 10px rgba(147, 213, 0, 0.0), 0 0 18px 4px rgba(147, 213, 0, 0.42);
              transform: scale(1.03);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(147, 213, 0, 0.0), 0 0 0 0 rgba(147, 213, 0, 0.0);
              transform: scale(1);
            }
          }`}
        </style>
        {!isPanelCollapsed && (
          <>
            <div
              className="screenshot-markup__header"
              style={{
                marginBottom: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <h2 className="screenshot-markup__title" style={{ marginBottom: 0 }}>
                Views
              </h2>
              <button
                type="button"
                className="btn btn-outline-danger project-status-modal__action-btn markup-detail-modal__action-btn"
                onClick={exitAppliedView}
                disabled={!viewerState?.viewer || !preAppliedViewContextRef.current || exitViewBusy}
                title="Exit the active view and restore your original viewer settings"
                style={{
                  padding: "3px 10px",
                  minHeight: "28px",
                  minWidth: "88px",
                  fontSize: "12px",
                  lineHeight: 1.1,
                }}
              >
                {exitViewBusy ? "Exiting..." : "Exit View"}
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                maxHeight: "calc(100vh - 150px)",
                minHeight: 0,
              }}
            >
              <div className="screenshot-markup__save-panel">
                <div
                  className="screenshot-markup__save-title"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "6px",
                  }}
                >
                  <span>Filters</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger screenshot-markup__filter-reset-btn"
                    style={{ minWidth: "72px" }}
                    disabled={!hasActiveFilters}
                    onClick={resetFilters}
                  >
                    Reset
                  </button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Select
                    className="save-markup-modal__select acc-project-select"
                    classNamePrefix="acc-project-select"
                    menuPortalTarget={document.body}
                    styles={customStyles}
                    options={viewFilterOptions}
                    value={viewFilterCat}
                    onChange={viewFilterHandler}
                    placeholder="Select a filter"
                  />
                  {viewFilterCat?.value === "discipline" ? (
                    <Select
                      className="save-markup-modal__select acc-project-select"
                      classNamePrefix="acc-project-select"
                      menuPortalTarget={document.body}
                      styles={customStyles}
                      options={availableDisciplineOptions}
                      value={
                        availableDisciplineOptions.find(
                          (option) => option.value === viewFilterValue,
                        ) || null
                      }
                      onChange={(option) => setViewFilterValue(option?.value || "")}
                      placeholder={
                        availableDisciplineOptions.length
                          ? "Select a discipline"
                          : "No saved disciplines"
                      }
                      isDisabled={!availableDisciplineOptions.length}
                    />
                  ) : viewFilterCat?.value === "creator" ? (
                    <Select
                      className="save-markup-modal__select acc-project-select"
                      classNamePrefix="acc-project-select"
                      menuPortalTarget={document.body}
                      styles={customStyles}
                      options={creatorOptions}
                      value={
                        creatorOptions.find((option) => option.value === viewFilterValue) || null
                      }
                      onChange={(option) => setViewFilterValue(option?.value || "")}
                      placeholder={creatorOptions.length ? "Select a creator" : "No saved creators"}
                      isDisabled={!creatorOptions.length}
                    />
                  ) : viewFilterCat?.value === "createdDate" ? (
                    <Select
                      className="save-markup-modal__select acc-project-select"
                      classNamePrefix="acc-project-select"
                      menuPortalTarget={document.body}
                      styles={customStyles}
                      options={createdDateOptions}
                      value={
                        createdDateOptions.find((option) => option.value === viewFilterValue) || null
                      }
                      onChange={(option) => setViewFilterValue(option?.value || "")}
                      placeholder={createdDateOptions.length ? "Select a date" : "No saved dates"}
                      isDisabled={!createdDateOptions.length}
                    />
                  ) : viewFilterCat?.value === "title" ? (
                    <input
                      type="text"
                      className="form-control save-markup-modal__input"
                      placeholder="Filter by title"
                      value={viewFilterValue}
                      onChange={(e) => setViewFilterValue(e.target.value)}
                    />
                  ) : null}
                </div>
              </div>

              <div
                className="screenshot-markup__save-panel"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: "4px",
                }}
              >
                <div className="markup-list screenshot-markup__save-list">
              {!savedViews.length && (
                <div className="screenshot-markup__save-state">
                  {savedViewsBusy
                    ? "Loading views..."
                    : "No saved views found for this project."}
                </div>
              )}

              {!!savedViews.length && !filteredViews.length && (
                <div className="screenshot-markup__save-state">
                  No saved views match the current filters.
                </div>
              )}

              {!!filteredViews.length &&
                filteredViews.map((savedView) => {
                  const isApplying = applyViewBusyId === savedView.id;
                  const disciplineDescription =
                    savedView?.disciplineDescription || "No discipline";
                  const createdOnLabel = formatViewDateLabel(savedView.createdAt) || "Unknown";
                  const createdByLabel = savedView.createdBy || "Unknown";
                  const statusRevisionChangeRows =
                    taskStatusRevisionChangesByViewId.get(savedView.id) || [];
                  const hasStatusRevisionChange = statusRevisionChangeRows.length > 0;

                  return (
                    <div
                      key={savedView.id}
                      className="card text-white bg-secondary mb-3 screenshot-markup__save-card"
                      style={{
                        borderColor:
                          activeViewId === savedView.id
                            ? "rgba(147, 213, 0, 0.72)"
                            : undefined,
                      }}
                    >
                      <div className="card-header markup-header screenshot-markup__save-card-header">
                        <div className="screenshot-markup__save-card-title">
                          {savedView.title}
                        </div>
                      </div>
                      <div
                        className="card-body screenshot-markup__save-card-content"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          columnGap: "16px",
                          rowGap: "6px",
                          padding: "10px 12px",
                        }}
                      >
                        {hasStatusRevisionChange && (
                          <div
                            style={{
                              width: "100%",
                              flexBasis: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "8px",
                            }}
                          >
                            <p
                              className="card-text markup-text"
                              style={{
                                marginBottom: "2px",
                                marginTop: "0px",
                                color: "#f5d48b",
                                flex: 1,
                              }}
                              title="Saved view revision differs from the live viewer task data."
                            >
                              View may need revising.
                            </p>
                            <button
                              type="button"
                              className="btn btn-outline-warning project-status-modal__action-btn markup-detail-modal__action-btn"
                              onClick={() => openModelChangeInfoModal(savedView)}
                              style={{
                                padding: "3px 10px",
                                minHeight: "28px",
                                fontSize: "12px",
                                lineHeight: 1.1,
                              }}
                              title="Show model changes and before/after details"
                            >
                              Info
                            </button>
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p className="card-text markup-text" style={{ marginBottom: "6px" }}>
                            Created on: {createdOnLabel}
                          </p>
                          <p className="card-text markup-text" style={{ marginBottom: "6px" }}>
                            Created By: {createdByLabel}
                          </p>
                          <p className="card-text markup-text" style={{ marginBottom: 0 }}>
                            Discipline: {disciplineDescription}
                          </p>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            alignItems: "stretch",
                            minWidth: "92px",
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-outline-success project-status-modal__action-btn markup-detail-modal__action-btn"
                            onClick={() => applySavedView(savedView)}
                            disabled={isApplying || !viewerState?.viewer}
                            style={{
                              padding: "4px 10px",
                              minHeight: "30px",
                              fontSize: "12px",
                              lineHeight: 1.2,
                            }}
                          >
                            {isApplying ? "Applying..." : "Apply"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger project-status-modal__action-btn markup-detail-modal__action-btn"
                            onClick={() => openDeleteViewModal(savedView)}
                            disabled={isApplying || deleteViewBusy}
                            style={{
                              padding: "4px 10px",
                              minHeight: "30px",
                              fontSize: "12px",
                              lineHeight: 1.2,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {viewerOverlayRoot &&
        createPortal(
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10000,
              pointerEvents: "none",
            }}
          >
            <button
              type="button"
              className="btn btn-sm btn-primary screenshot-markup__start-btn"
              onClick={openSaveViewModal}
              disabled={!viewerState?.viewer}
              title="Capture current view"
              aria-label="Capture current view"
              style={{
                position: "absolute",
                top: "10px",
                left: "10px",
                zIndex: 1507,
                pointerEvents: "auto",
                width: "38px",
                height: "30px",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "mwhMarkupPing 1.5s ease-out infinite",
                borderColor: "#93d500",
                backgroundColor: "#93d500",
                color: "#ffffff",
                fontWeight: 700,
              }}
            >
              +
            </button>
          </div>,
          viewerOverlayRoot,
        )}

      <Modal
        show={saveViewModalOpen}
        onHide={closeSaveViewModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>View Additional Information</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeSaveViewModal}
              aria-label="Close"
              disabled={saveViewBusy}
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <div
            className="form-group save-markup-modal__form"
            style={{ marginBottom: "10px" }}
          >
            <div className="form-floating mb-1">
              <input
                type="text"
                className="form-control save-markup-modal__input"
                id="views_panel_view_title"
                placeholder="View Title"
                value={saveViewTitle}
                onChange={(e) => setSaveViewTitle(e.target.value)}
              />
              <label
                htmlFor="views_panel_view_title"
                className="save-markup-modal__floating-label"
              >
                View title
              </label>
            </div>
          </div>

          <div
            className="form-group save-markup-modal__form"
            style={{ marginBottom: "10px" }}
          >
            <label
              htmlFor="views_panel_view_discipline"
              className="form-label save-markup-modal__label"
            >
              Discipline
            </label>
            <Select
              className="save-markup-modal__select acc-project-select"
              classNamePrefix="acc-project-select"
              inputId="views_panel_view_discipline"
              menuPortalTarget={document.body}
              styles={customStyles}
              options={disciplineOptions}
              value={saveViewDiscipline}
              onChange={setSaveViewDiscipline}
              placeholder={
                disciplineOptions.length
                  ? "Select a discipline"
                  : "No disciplines available"
              }
              isDisabled={saveViewBusy || !disciplineOptions.length}
            />
          </div>

          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            <button
              disabled={
                saveViewBusy ||
                !saveViewTitle.trim() ||
                !capturedViewState ||
                !saveViewDiscipline
              }
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={submitSaveViewModal}
            >
              {saveViewBusy ? (
                <>
                  Saving...
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginLeft: "8px" }}
                  />
                </>
              ) : (
                "Save View"
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeSaveViewModal}
              disabled={saveViewBusy}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={deleteViewModalOpen}
        onHide={closeDeleteViewModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Delete View</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeDeleteViewModal}
              aria-label="Close"
              disabled={deleteViewBusy}
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <p style={{ marginBottom: "12px" }}>
            Are you sure you want to delete this saved view?
          </p>
          <p
            style={{
              marginBottom: "14px",
              fontSize: "13px",
              color: "rgba(255, 255, 255, 0.8)",
            }}
          >
            {pendingDeleteView?.title || "Untitled view"}
          </p>

          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={confirmDeleteSavedView}
              disabled={!pendingDeleteView?.id || deleteViewBusy}
            >
              {deleteViewBusy ? (
                <>
                  Deleting...
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginLeft: "8px" }}
                  />
                </>
              ) : (
                "Delete"
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeDeleteViewModal}
              disabled={deleteViewBusy}
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={modelChangeInfoModalOpen}
        onHide={closeModelChangeInfoModal}
        dialogClassName="project-info-modal"
        centered
        size="xl"
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Model Change Info</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeModelChangeInfoModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <p style={{ marginBottom: "12px" }}>
            {selectedViewForModelChangeInfo?.title || "Saved view"}
          </p>
          <p
            style={{
              marginBottom: "12px",
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: "13px",
            }}
          >
            Rows appear only when revision changed between when the view was saved and the current live model data.
          </p>
          <div
            className="ag-theme-alpine"
            style={{
              width: "100%",
              height: "420px",
              border: "1px solid rgba(255, 255, 255, 0.12)",
            }}
          >
            <AgGridReact
              theme={agGridTheme}
              rowData={modelChangeRows}
              columnDefs={modelChangeGridColumnDefs}
              getRowId={(params) => params.data.id}
              gridOptions={{
                ...agGridOptionsSingle,
                rowSelection: undefined,
                pagination: false,
                domLayout: "normal",
              }}
            />
          </div>
          {!modelChangeRows.length && (
            <p
              style={{
                marginTop: "10px",
                marginBottom: 0,
                color: "rgba(255, 255, 255, 0.75)",
                fontSize: "13px",
              }}
            >
              No model differences were detected at this time.
            </p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Views;
